import styles from "./StickyCartBar.module.css";
import Price from "../common/Price.tsx";
import { useViewModel, useIsB2B } from "../../hooks/useViewModel";
import { ArrowRight, Flex, Share } from "../common/icons";
import { formatPrice, selectPrice } from "../../utils/priceHelpers.ts";

interface StickyCartBarProps {
	onAddToCart: () => void;
	onFlexAddToCart: () => void;
	onOpenFlexPopup: () => void;
	onShareConfiguration?: () => void;
	isLoading?: boolean;
	isIncompatible?: boolean;
	currentPrice?: {
		net: number;
		gross: number;
	};
	flexPrice?: {
		net: number;
		gross: number;
	} | null;
}

const StickyCartBar: React.FC<StickyCartBarProps> = ({
	onAddToCart,
	onFlexAddToCart,
	onOpenFlexPopup,
	onShareConfiguration,
	isLoading = false,
	isIncompatible = false,
	currentPrice,
	flexPrice,
}) => {
	const { data } = useViewModel();
	const { viewModel, strings } = data!;
	const isB2B = useIsB2B();

	if (!viewModel) {
		return null;
	}

	const price = currentPrice
		? selectPrice(currentPrice, isB2B)
		: selectPrice({ net: viewModel.price.customerPriceNet, gross: viewModel.price.customerPrice }, isB2B);

	const savings = isB2B ? (viewModel.price.discountNet ?? 0) : (viewModel.price.discount ?? 0);

	const listPrice = (price ?? 0) + (savings ?? 0);

	const monthly = flexPrice?.gross ?? viewModel.price.subscriptionPrice ?? 0;

	return (
		<div className={styles.cartBar}>
			<div className={styles.container}>
				<div className={styles.section}>
					{onShareConfiguration && (
						<button
							className={styles.secondaryButton}
							onClick={onShareConfiguration}
							disabled={isLoading}
							data-testid="sticky-bar-share-configuration-button"
						>
							<Share aria-hidden="true" />
							{strings.product.configBundles.shareConfiguration}
						</button>
					)}
				</div>
				<div className={styles.section}>
					<div className={styles.priceSection}>
						<Price
							price={price}
							savings={savings}
							listPrice={listPrice}
							size="large"
						/>
					</div>
					<button
						className={`${styles.ctaButton} btn btn-large primary`}
						onClick={onAddToCart}
						disabled={isLoading || isIncompatible}
						data-testid="sticky-bar-add-to-cart-button"
					>
						{strings.product.configBundles.buyButton}
					</button>
					{monthly > 0 && (
						<>
							<button
								className={styles.flexButton}
								onClick={onFlexAddToCart}
								disabled={isLoading || isIncompatible}
								data-testid="sticky-bar-flex-add-to-cart-button"
							>
								<span
									dangerouslySetInnerHTML={{
										__html:
											strings.product.configBundles.subscriptionBuyButton.replace(
												"{monthlyPrice}",
												` ${String(formatPrice(monthly, "nb-NO"))},-`,
											),
									}}
								></span>
							</button>
							<button
								className={styles.flexInfoButton}
								onClick={onOpenFlexPopup}
								disabled={isLoading || isIncompatible}
								data-testid="sticky-bar-flex-information-button"
							>
								<span className={styles.flexButtonText}>
									{strings.product.configBundles.shortAbout}
								</span>
								<Flex aria-hidden="true" />
								<ArrowRight aria-hidden="true" />
							</button>
						</>
					)}
				</div>
			</div>
		</div>
	);
};

export default StickyCartBar;